import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { Task } from '@sketchnote/gantt-chart';
import { AddIcon, ChevronDownIcon } from '@sketchnote/icons';
import { Button, Checkbox, CheckboxKanbanItem } from '@sketchnote/shared-components';
import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { isFreeUser } from 'src/constants/plans';
import { useBoardStore } from 'src/features/boards';
import { useKanbanStore } from 'src/features/kanban';
import { ColumnToolbar } from 'src/features/kanban/component/Column/Toolbar';
import { CreateTask } from 'src/features/kanban/component/Item';
import { KanbanState } from 'src/features/kanban/store/types';
import { useAuth } from 'src/shared/hooks/use-auth';
import { truncateString } from 'src/utils/truncateString';

const { useCallback, useState } = React;

export interface TaskListHeaderProps {
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  locale: string;
  tasks: Task[];
  selectedTaskId: string;
  setSelectedTask: (taskId: string) => void;
  onExpanderClick: (task: Task) => void;
}

export const TaskListTable = ({ rowHeight, tasks, onExpanderClick }: TaskListHeaderProps) => {
  const router = useRouter();
  const boardData = useBoardStore();
  const {
    onMarkAsComplete,
    onChangeCurrentSelectedItem,
    onAddNewColumn,
    isReadOnly,
    onDeleteColumn,
    onChangeColumnColor,
    currentSelectedItem,
    onCloseCurrentSelectedItem,
  } = useKanbanStore();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const authData = useAuth((s) => s.userDetails);
  const handleTaskViewOpen = (
    columnId: KanbanState['currentSelectedItem']['columnId'],
    index: KanbanState['currentSelectedItem']['itemIndex'],
    mode: KanbanState['currentSelectedItem']['mode']
  ) => {
    onChangeCurrentSelectedItem({ isOpen: true, columnId, itemIndex: index, mode: mode });
  };

  const getLinkHref = useCallback(
    (item) => {
      let link = '';

      if (boardData.isPublicView) {
        //public
        link = `/${mode}/${orgId}/files/${router.query.slug}/public?view=${router.query.view}&type=${router.query.type}&taskId=${item.id}`;
      } else {
        link = `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}&taskId=${item.id}`;
      }
      return link;
    },
    [router.query.slug]
  );

  const onColumnChange = (columnId: string) => (e) => {
    if (e.value === 'delete') {
      onDeleteColumn({ columnId: columnId });
    } else if (e.value != 'colour-picker') {
      onChangeColumnColor({ id: columnId, color: e.value });
    }
  };

  return (
    <div className="mx-24 mb-24">
      {tasks.map((t, index) => {
        if (t.hideChildren !== undefined) {
          const showAddTaskButton =
            tasks.length - 1 === index ||
            tasks[index + 1]?.hideChildren !== undefined ||
            tasks.length - 1 === index ||
            (authData?.plan?.type &&
              isFreeUser(authData?.plan?.type) &&
              boardData.totalTaskCount < 10);
          return (
            <div
              style={{ height: rowHeight }}
              className="flex flex-col justify-center"
              key={`${t.id}row`}
            >
              <div
                className="flex text-sm items-center justify-between border-b w-full pb-5"
                role="button"
                onClick={() => onExpanderClick(t)}
              >
                <div>
                  <ChevronDownIcon
                    wrapperClass={cn('transform transition-transform duration-150', {
                      '-rotate-90': t.hideChildren,
                    })}
                  />
                  <span
                    className="ml-10 px-8 py-1 bg-red-200 rounded"
                    style={{ backgroundColor: t.styles?.backgroundColor }}
                    title={t.name}
                  >
                    {truncateString(t.name, 28)}
                  </span>
                </div>
                <div className="mr-10 text-gray-500">
                  {!isReadOnly && boardData.access !== 'view' ? (
                    <ColumnToolbar onClick={onColumnChange(t.id)} />
                  ) : null}
                </div>
              </div>
              {!t.hideChildren && t.data?.unscheduled.length > 0 && (
                <Popover>
                  <PopoverTrigger>
                    <button className="text-primary-500 text-left">
                      Unscheduled tasks ( {t.data?.unscheduled.length} )
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="overflow-auto h-200">
                    <div className="flex flex-col p-12 text-sm rounded-lg bg-white shadow-sm min-w-303">
                      <p className="mb-16 text-gray-500">Select task and add dates</p>
                      {t.data?.unscheduled.map((item) => (
                        <Link href={getLinkHref(item)} replace key={item.id}>
                          <a className="text-gray-999 mb-20 hover:text-primary-999">
                            <span>{truncateString(item.name, 28)}</span>
                          </a>
                        </Link>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {showAddTaskButton && !t.hideChildren && !isReadOnly && boardData.access !== 'view' && (
                <Popover placement="right-start">
                  <PopoverTrigger>
                    <button
                      className="flex w-full hover:bg-gray-100 rounded p-5 mt-2"
                      tabIndex={-1}
                      onClick={() => {
                        handleTaskViewOpen(t?.id, undefined, 'add');
                      }}
                    >
                      <AddIcon wrapperClass="addtask text-gray-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <CreateTask />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        } else {
          const showAddTaskButton =
            tasks[index + 1]?.hideChildren !== undefined || tasks.length - 1 === index;
          return (
            <div
              style={{ height: rowHeight }}
              className="flex flex-col w-full justify-center"
              key={`${t.id}row`}
            >
              <Link href={getLinkHref(t)} replace>
                <a className="w-full text-sm">
                  <div className="flex items-center rounded bg-white border border-gray-200 px-10 py-8 w-full">
                    <div onClick={(e) => e.stopPropagation()}>
                      <CheckboxKanbanItem
                        checked={t?.data?.completed}
                        isDisabled={
                          t.data?.isReadOnly || t.data?.access === 'view' || boardData.isPublicView
                        }
                        color="#33CC48"
                        onChange={(e) => {
                          onMarkAsComplete({
                            columnId: t?.data?.column,
                            itemId: t?.data?.id || '',
                            completed: e,
                          });
                        }}
                      />
                    </div>
                    <span className="ml-5" title={t.name}>
                      {truncateString(t.name, 28)}
                    </span>
                  </div>
                </a>
              </Link>
              {showAddTaskButton && !isReadOnly && boardData.access !== 'view' && (
                <Popover placement="right-start">
                  <PopoverTrigger>
                    <button
                      className="flex w-full hover:bg-gray-100 rounded p-5 mt-2"
                      tabIndex={-1}
                      onClick={() => {
                        handleTaskViewOpen(t?.data?.column, undefined, 'add');
                      }}
                    >
                      <AddIcon wrapperClass="addtask text-gray-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <CreateTask />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        }
      })}

      {!isReadOnly &&
        boardData.access !== 'view' &&
        authData?.plan?.type &&
        !isFreeUser(authData?.plan?.type) && (
          <Button iconLeft={<AddIcon />} onClick={onAddNewColumn} className="mt-20" status="ghost">
            Add column
          </Button>
        )}
    </div>
  );
};
